import msalInstance, { loginRequest } from './msalConfig';

/**
 * MSAL-backed identity provider for Microsoft login.
 *
 * `login()` uses the redirect flow (per FR-002): calling it navigates the browser away, so its
 * promise does not resolve before the page unloads. The result of a completed login is instead
 * picked up on the next page load via `handleRedirectResult()`, which every consumer of this
 * provider must call once on mount.
 *
 * There is no `logout()` here on purpose: the app's own logout only clears the internal JWT
 * (`token` in localStorage) — it does not sign the user out of their Microsoft account. Only the
 * locally-tracked MSAL account is cleared, via `clearInteractionState()` below, when needed.
 */

async function login() {
  await msalInstance.loginRedirect(loginRequest);
}

/**
 * Must be called on mount by any page that can be the target of the Microsoft redirect (i.e. the
 * login screen). Resolves with an `AuthResult` when this page load is the return leg of a
 * successful login, or `null` when it isn't a redirect return at all. Rejects when the user
 * cancelled, closed the window, or Microsoft returned an error.
 */
async function handleRedirectResult() {
  const result = await msalInstance.handleRedirectPromise();

  if (!result) {
    return null;
  }

  msalInstance.setActiveAccount(result.account);

  return {
    idToken: result.idToken,
    account: {
      name: result.account?.name,
      email: result.account?.username,
    },
  };
}

async function isAuthenticated() {
  return msalInstance.getAllAccounts().length > 0;
}

/**
 * Clears the locally-tracked active account without redirecting to Microsoft's own logout page.
 * Used after a backend rejection (FR-009) so the user can retry cleanly, without the disruption
 * of a full Microsoft sign-out round trip.
 */
function clearInteractionState() {
  msalInstance.setActiveAccount(null);
}

async function initialize() {
  await msalInstance.initialize();
}

const microsoftIdentityProvider = {
  login,
  isAuthenticated,
  handleRedirectResult,
  clearInteractionState,
  initialize,
};

export default microsoftIdentityProvider;
